/**
 * Structured content inside a message.
 *
 * ## The boundary, stated once
 *
 * The channel-agnostic block types — a time-frame note, "availability requested", the
 * availability the homeowner shared — render HERE, because they are messaging concepts
 * that read one messaging-adjacent row and display it.
 *
 * Visit cards do NOT. A visit block needs the visit's live status, deep-links into a
 * route the surface owns (`?main-tab=visits&visit-id=…` in the office, a bottom sheet in
 * the member portal) and, on the member side, carries the accept/decline mutation. Those
 * are order-domain UI, so the surface supplies them through `renderBlock` and this
 * component is only the dispatcher.
 *
 * Order of resolution: the surface's `renderBlock` wins; returning `null`/`undefined`
 * falls through to the default set below; an unknown type renders nothing.
 */

import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { Badge } from "@/components/ui/badge/badge";
import { BwTimeDashLine } from "@/components/ui/icons";
import { messageCardClassName } from "@/components/ui/message/message-card";
import { useOrderRequesterTimeZone } from "@/hooks/use-order-requester-timezone";
import { getDisplayDateTimeWithSlot } from "@/libs/date-fns";
import type { Channel } from "../_api/channel-endpoints";
import {
  toTimePreferenceSlots,
  useChannelTimePreference,
} from "../_api/use-channel-time-preference";
import { orderIdOfChannel } from "../_libs/channel-cid";
import type { ChannelBlockContext } from "../_types/channel-surface";

type ChannelBlockProps = ChannelBlockContext & {
  renderBlock?: (context: ChannelBlockContext) => React.ReactNode;
};

export function ChannelBlock({ renderBlock, ...context }: ChannelBlockProps) {
  const surfaceRendered = renderBlock?.(context);
  if (surfaceRendered != null) return <>{surfaceRendered}</>;

  const payload = (context.block.payload ?? {}) as {
    timePreferenceId?: string;
    message?: string;
  };

  switch (context.block.type) {
    case "TIME_FRAME":
      return (
        <TimeFrameBlock
          message={payload.message ?? ""}
          className={context.cardClassName}
        />
      );
    case "TIME_PREFERENCE_REQUESTED":
      return (
        <TimePreferenceRequestedBlock
          timePreferenceId={payload.timePreferenceId ?? ""}
          className={context.cardClassName}
        />
      );
    case "TIME_PREFERENCE_SHARED":
      return (
        <TimePreferenceSharedBlock
          timePreferenceId={payload.timePreferenceId ?? ""}
          channel={context.channel}
          className={context.cardClassName}
        />
      );
    default:
      return null;
  }
}

function TimeFrameBlock({
  message,
  className,
}: {
  message: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        /* sorted */
        messageCardClassName,
        "px-4 py-3",
        "text-sm",
        className,
      )}
    >
      {message}
    </div>
  );
}

function TimePreferenceRequestedBlock({
  timePreferenceId,
  className,
}: {
  timePreferenceId: string;
  className?: string;
}) {
  const { data: timePreference } = useChannelTimePreference(timePreferenceId);

  const slots = toTimePreferenceSlots(timePreference?.timePreferenceItems);
  const isActive = timePreference?.isActive ?? false;
  const isAwaitingAnswer = isEmpty(slots) && isActive;

  return (
    <div
      className={cn(
        /* sorted */
        messageCardClassName,
        "flex items-center gap-2",
        "p-4",
        className,
      )}
    >
      <BwTimeDashLine
        size={16}
        className={cn("text-text-dark-high", !isActive && "text-text-dark-low")}
      />
      <p
        className={cn(
          /* sorted */
          "grow",
          "text-sm text-text-dark-high",
          !isActive && "text-text-dark-low",
        )}
      >
        Availability requested
      </p>
      {isAwaitingAnswer && (
        <Badge shape="fill" color="orange" size="sm">
          Pending
        </Badge>
      )}
    </div>
  );
}

/**
 * The windows the homeowner offered.
 *
 * Formatted in the ORDER REQUESTER's timezone — the same source every visit card in the
 * thread uses ({@link useOrderRequesterTimeZone}). Formatting this one card in the
 * VIEWER's timezone instead (what V4 did) printed two different clock times for the same
 * instant a few pixels apart, because the visit card above it was already requester-timed.
 */
function TimePreferenceSharedBlock({
  timePreferenceId,
  channel,
  className,
}: {
  timePreferenceId: string;
  channel: Channel;
  className?: string;
}) {
  const timeZone = useOrderRequesterTimeZone(orderIdOfChannel(channel));
  const { data: timePreference, isLoading } =
    useChannelTimePreference(timePreferenceId);

  if (isLoading) return null;

  const slots = toTimePreferenceSlots(timePreference?.timePreferenceItems);
  if (isEmpty(slots)) return null;

  return (
    <div
      className={cn(
        /* sorted */
        messageCardClassName,
        "divide-y divide-border-dark-min",
        className,
      )}
    >
      {slots.map((slot, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: slots carry no id on the wire
          key={index}
          className="flex items-center h-12 px-4 text-sm"
        >
          {getDisplayDateTimeWithSlot(slot, timeZone).toString()}
        </div>
      ))}
    </div>
  );
}
